import type { OperationalFactInput } from '../facts/operational-facts.ts';
import type { AvailabilityStatus, BusinessPolicySnapshot } from '../policy/business-policy.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type { StoreDay, StoreSettingsPayload } from '../store-settings/store-settings.ts';

export type AvailabilityWriterSurface = 'smt' | 'smm' | 'admin';
export type AvailabilityProvenanceSource = AvailabilityWriterSurface | 'system' | 'policy';

export type AvailabilityReasonCode =
  | 'manual_soldout'
  | 'manual_restore'
  | 'production_pause'
  | 'production_resume'
  | 'scheduled_restore'
  | 'daily_soldout_restore'
  | 'manager_override';

export interface AvailabilityCommandContext {
  readonly actorId: string;
  readonly sourceSurface: AvailabilityWriterSurface;
  readonly at: string;
  readonly expectedRevision: number;
  readonly idempotencyKey: string;
}

export interface SetAvailabilityNodesCommand {
  readonly nodeIds: readonly string[];
  readonly status: AvailabilityStatus;
  readonly reasonCode: AvailabilityReasonCode;
  readonly reasonNote?: string | null;
}

export interface CreateRestoreScheduleCommand {
  readonly scheduleId: string;
  readonly nodeIds: readonly string[];
  readonly restoreAt: string;
}

export interface UpdateRestoreScheduleCommand extends CreateRestoreScheduleCommand {}

export interface CancelRestoreScheduleCommand {
  readonly scheduleId: string;
}

export interface ConfigureDailySoldoutRestorePolicyCommand {
  readonly enabled: boolean;
  readonly restoreLocalTime: string | null;
}

export interface ApplyDueRestoresCommand {
  readonly now: string;
  readonly expectedRevision: number;
  readonly idempotencyKey: string;
}

export type RestoreScheduleStatus = 'active' | 'cancelled' | 'applied' | 'skipped';

export interface RestoreScheduleProjection {
  readonly scheduleId: string;
  readonly nodeIds: readonly string[];
  readonly restoreAt: string;
  readonly status: RestoreScheduleStatus;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly sourceSurface: AvailabilityWriterSurface;
}

export interface DailySoldoutRestorePolicyProjection {
  readonly enabled: boolean;
  readonly restoreLocalTime: string | null;
  readonly lastAppliedBusinessDate: string | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

export interface AvailabilityNodeProjection {
  readonly nodeId: string;
  readonly status: AvailabilityStatus;
  readonly operationalOverride: boolean;
  readonly changedAt: string | null;
  readonly changedBy: string | null;
  readonly source: AvailabilityProvenanceSource;
  readonly reasonCode: AvailabilityReasonCode | null;
  readonly reasonNote: string | null;
}

export interface AvailabilityProjection {
  readonly storeId: string;
  readonly revision: number;
  readonly nodes: readonly AvailabilityNodeProjection[];
  readonly schedules: readonly RestoreScheduleProjection[];
  readonly dailySoldoutRestorePolicy: DailySoldoutRestorePolicyProjection;
}

export interface AvailabilityAuthorizationRequest {
  readonly actorId: string;
  readonly permission: StaffPermission;
  readonly at: string;
}

export interface AvailabilityAuthorityOptions {
  readonly storeId: string;
  readonly authorize: (request: AvailabilityAuthorizationRequest) => void;
  readonly getBasePolicy: () => BusinessPolicySnapshot;
  readonly getStoreSettings: () => StoreSettingsPayload;
  readonly appendFact: (input: OperationalFactInput) => unknown;
}

interface AvailabilityOverride {
  readonly status: AvailabilityStatus;
  readonly changedAt: string;
  readonly changedBy: string;
  readonly source: AvailabilityWriterSurface | 'system';
  readonly reasonCode: AvailabilityReasonCode;
  readonly reasonNote: string | null;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: AvailabilityProjection;
}

interface LocalStoreClock {
  readonly businessDate: string;
  readonly day: StoreDay;
  readonly localTime: string;
}

const AVAILABILITY_STATUSES: readonly AvailabilityStatus[] = ['available', 'soldout', 'paused'];
const WRITER_SURFACES: readonly AvailabilityWriterSurface[] = ['smt', 'smm', 'admin'];
const REASON_CODES: readonly AvailabilityReasonCode[] = [
  'manual_soldout',
  'manual_restore',
  'production_pause',
  'production_resume',
  'scheduled_restore',
  'daily_soldout_restore',
  'manager_override',
];
const STORE_DAY_BY_WEEKDAY: Readonly<Record<string, StoreDay>> = Object.freeze({
  Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun',
});

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string, code: string): string => {
  if (!value?.trim()) throw new Error(code);
  return value.trim();
};

const assertTimestamp = (value: string, code = 'AVAILABILITY_TIMESTAMP_INVALID'): string => {
  if (!Number.isFinite(Date.parse(value))) throw new Error(code);
  return value;
};

const assertRevision = (value: number): void => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('AVAILABILITY_EXPECTED_REVISION_INVALID');
};

const normalizeReasonNote = (value: string | null | undefined): string | null => {
  if (value === undefined || value === null) return null;
  const normalized = value.trim();
  return normalized || null;
};

const normalizedTargets = (nodeIds: readonly string[]): string[] => {
  if (!Array.isArray(nodeIds) || nodeIds.length === 0) throw new Error('AVAILABILITY_TARGETS_REQUIRED');
  const normalized = nodeIds.map((nodeId) => assertText(nodeId, 'AVAILABILITY_NODE_ID_REQUIRED'));
  if (new Set(normalized).size !== normalized.length) throw new Error('AVAILABILITY_TARGET_DUPLICATE');
  return [...normalized].sort();
};

const isLocalTime = (value: string | null): value is string =>
  typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);

const localStoreClockAt = (iso: string, settings: StoreSettingsPayload): LocalStoreClock => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: settings.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date(assertTimestamp(iso)))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  const day = STORE_DAY_BY_WEEKDAY[parts.weekday];
  if (!day) throw new Error('AVAILABILITY_STORE_DAY_INVALID');
  return {
    businessDate: `${parts.year}-${parts.month}-${parts.day}`,
    day,
    localTime: `${parts.hour}:${parts.minute}`,
  };
};

const businessDateAt = (iso: string, settings: StoreSettingsPayload): string =>
  localStoreClockAt(iso, settings).businessDate;

export function createAvailabilityAuthority(options: AvailabilityAuthorityOptions) {
  const storeId = assertText(options.storeId, 'AVAILABILITY_STORE_ID_REQUIRED');
  let revision = 0;
  const overrides = new Map<string, AvailabilityOverride>();
  const schedules = new Map<string, RestoreScheduleProjection>();
  const idempotency = new Map<string, IdempotentResult>();

  let dailyPolicy: DailySoldoutRestorePolicyProjection = {
    enabled: false,
    restoreLocalTime: null,
    lastAppliedBusinessDate: null,
    updatedAt: null,
    updatedBy: null,
  };

  const assertNodeKnown = (nodeId: string, base: BusinessPolicySnapshot): AvailabilityStatus => {
    const status = base.availability.nodes[nodeId];
    if (!status || !AVAILABILITY_STATUSES.includes(status)) {
      throw new Error(`AVAILABILITY_NODE_NOT_FOUND:${nodeId}`);
    }
    return status;
  };

  const isNodeKnown = (nodeId: string, base: BusinessPolicySnapshot): boolean => {
    const status = base.availability.nodes[nodeId];
    return Boolean(status && AVAILABILITY_STATUSES.includes(status));
  };

  const validateTargetsAgainstBase = (nodeIds: readonly string[], base: BusinessPolicySnapshot): void => {
    for (const nodeId of nodeIds) assertNodeKnown(nodeId, base);
  };

  const effectiveStatusFrom = (
    sourceOverrides: ReadonlyMap<string, AvailabilityOverride>,
    nodeId: string,
    base: BusinessPolicySnapshot,
  ): AvailabilityStatus => sourceOverrides.get(nodeId)?.status ?? assertNodeKnown(nodeId, base);

  const effectiveStatus = (nodeId: string, base: BusinessPolicySnapshot): AvailabilityStatus =>
    effectiveStatusFrom(overrides, nodeId, base);

  const projectionFrom = (
    sourceRevision: number,
    sourceOverrides: ReadonlyMap<string, AvailabilityOverride>,
    sourceSchedules: ReadonlyMap<string, RestoreScheduleProjection>,
    sourceDailyPolicy: DailySoldoutRestorePolicyProjection,
  ): AvailabilityProjection => {
    const base = options.getBasePolicy();
    const nodeIds = Object.keys(base.availability.nodes).sort();
    const nodes = nodeIds.map((nodeId): AvailabilityNodeProjection => {
      const baseline = assertNodeKnown(nodeId, base);
      const operational = sourceOverrides.get(nodeId);
      if (!operational) {
        return {
          nodeId,
          status: baseline,
          operationalOverride: false,
          changedAt: null,
          changedBy: null,
          source: 'policy',
          reasonCode: null,
          reasonNote: null,
        };
      }
      return {
        nodeId,
        status: operational.status,
        operationalOverride: true,
        changedAt: operational.changedAt,
        changedBy: operational.changedBy,
        source: operational.source,
        reasonCode: operational.reasonCode,
        reasonNote: operational.reasonNote,
      };
    });

    return {
      storeId,
      revision: sourceRevision,
      nodes,
      schedules: [...sourceSchedules.values()]
        .sort((left, right) => left.scheduleId.localeCompare(right.scheduleId))
        .map((schedule) => clone(schedule)),
      dailySoldoutRestorePolicy: clone(sourceDailyPolicy),
    };
  };

  const currentProjection = (): AvailabilityProjection =>
    projectionFrom(revision, overrides, schedules, dailyPolicy);

  const assertContext = (context: AvailabilityCommandContext): void => {
    assertText(context.actorId, 'AVAILABILITY_ACTOR_REQUIRED');
    if (!WRITER_SURFACES.includes(context.sourceSurface)) throw new Error('AVAILABILITY_SOURCE_SURFACE_INVALID');
    assertTimestamp(context.at);
    assertRevision(context.expectedRevision);
    assertText(context.idempotencyKey, 'AVAILABILITY_IDEMPOTENCY_REQUIRED');
  };

  const replay = (key: string, signature: string): AvailabilityProjection | null => {
    const existing = idempotency.get(key);
    if (!existing) return null;
    if (existing.signature !== signature) throw new Error('AVAILABILITY_IDEMPOTENCY_CONFLICT');
    return clone(existing.result);
  };

  const commitIdempotency = (key: string, signature: string, result: AvailabilityProjection): AvailabilityProjection => {
    idempotency.set(key, { signature, result: clone(result) });
    return clone(result);
  };

  const appendControlFact = (
    factType: string,
    nextRevision: number,
    at: string,
    payload: unknown,
    suffix: string,
  ): void => {
    options.appendFact({
      eventId: `${storeId}:availability:${nextRevision}:${suffix}`,
      aggregateType: 'availability',
      aggregateId: storeId,
      factType,
      businessDate: businessDateAt(at, options.getStoreSettings()),
      occurredAt: at,
      recordedAt: at,
      schemaVersion: 1,
      source: { authority: 'availability', revision: String(nextRevision) },
      payload,
    });
  };

  const assertScheduleCommand = (
    command: CreateRestoreScheduleCommand,
    context: AvailabilityCommandContext,
  ): { readonly scheduleId: string; readonly nodeIds: string[]; readonly restoreAt: string } => {
    const scheduleId = assertText(command.scheduleId, 'AVAILABILITY_SCHEDULE_ID_REQUIRED');
    const nodeIds = normalizedTargets(command.nodeIds);
    const restoreAt = assertTimestamp(command.restoreAt, 'AVAILABILITY_RESTORE_AT_INVALID');
    if (Date.parse(restoreAt) <= Date.parse(context.at)) throw new Error('AVAILABILITY_RESTORE_AT_NOT_FUTURE');
    validateTargetsAgainstBase(nodeIds, options.getBasePolicy());
    return { scheduleId, nodeIds, restoreAt };
  };

  const commitState = (
    nextRevision: number,
    nextOverrides: ReadonlyMap<string, AvailabilityOverride>,
    nextSchedules: ReadonlyMap<string, RestoreScheduleProjection>,
    nextDailyPolicy: DailySoldoutRestorePolicyProjection,
  ): void => {
    const overrideSnapshot = [...nextOverrides.entries()].map(([nodeId, value]) => [nodeId, clone(value)] as const);
    const scheduleSnapshot = [...nextSchedules.entries()].map(([scheduleId, value]) => [scheduleId, clone(value)] as const);
    const dailyPolicySnapshot = clone(nextDailyPolicy);

    overrides.clear();
    for (const [nodeId, value] of overrideSnapshot) overrides.set(nodeId, value);
    schedules.clear();
    for (const [scheduleId, value] of scheduleSnapshot) schedules.set(scheduleId, value);
    dailyPolicy = dailyPolicySnapshot;
    revision = nextRevision;
  };

  return {
    setNodes(command: SetAvailabilityNodesCommand, context: AvailabilityCommandContext): AvailabilityProjection {
      assertContext(context);
      const nodeIds = normalizedTargets(command.nodeIds);
      if (!AVAILABILITY_STATUSES.includes(command.status)) throw new Error('AVAILABILITY_STATUS_INVALID');
      if (!REASON_CODES.includes(command.reasonCode)) throw new Error('AVAILABILITY_REASON_INVALID');
      const reasonNote = normalizeReasonNote(command.reasonNote);
      const signature = signatureOf({ kind: 'availability.set', command: { ...command, nodeIds, reasonNote }, context });
      const replayed = replay(context.idempotencyKey, signature);
      if (replayed) return replayed;

      options.authorize({ actorId: context.actorId, permission: 'availability.change', at: context.at });
      if (context.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');

      const base = options.getBasePolicy();
      const previous = nodeIds.map((nodeId) => ({ nodeId, status: effectiveStatus(nodeId, base) }));
      const nextRevision = revision + 1;
      const nextOverrides = new Map(overrides);
      for (const nodeId of nodeIds) {
        nextOverrides.set(nodeId, {
          status: command.status,
          changedAt: context.at,
          changedBy: context.actorId,
          source: context.sourceSurface,
          reasonCode: command.reasonCode,
          reasonNote,
        });
      }

      appendControlFact('availability.changed', nextRevision, context.at, {
        actorId: context.actorId,
        sourceSurface: context.sourceSurface,
        reasonCode: command.reasonCode,
        reasonNote,
        nodeIds,
        transitions: previous.map((entry) => ({
          nodeId: entry.nodeId,
          previousStatus: entry.status,
          newStatus: command.status,
        })),
      }, 'changed');

      commitState(nextRevision, nextOverrides, schedules, dailyPolicy);
      return commitIdempotency(context.idempotencyKey, signature, currentProjection());
    },

    createRestoreSchedule(
      command: CreateRestoreScheduleCommand,
      context: AvailabilityCommandContext,
    ): AvailabilityProjection {
      assertContext(context);
      const accepted = assertScheduleCommand(command, context);
      const signature = signatureOf({ kind: 'availability.schedule.create', command: accepted, context });
      const replayed = replay(context.idempotencyKey, signature);
      if (replayed) return replayed;

      options.authorize({ actorId: context.actorId, permission: 'availability.schedule', at: context.at });
      if (context.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');
      if (schedules.has(accepted.scheduleId)) throw new Error('AVAILABILITY_SCHEDULE_ID_CONFLICT');

      const nextRevision = revision + 1;
      const schedule: RestoreScheduleProjection = {
        ...accepted,
        status: 'active',
        createdAt: context.at,
        createdBy: context.actorId,
        updatedAt: context.at,
        updatedBy: context.actorId,
        sourceSurface: context.sourceSurface,
      };
      const nextSchedules = new Map(schedules);
      nextSchedules.set(schedule.scheduleId, schedule);
      appendControlFact('availability.restore_schedule.changed', nextRevision, context.at, {
        operation: 'create', actorId: context.actorId, sourceSurface: context.sourceSurface, schedule,
      }, `schedule-${accepted.scheduleId}-create`);

      commitState(nextRevision, overrides, nextSchedules, dailyPolicy);
      return commitIdempotency(context.idempotencyKey, signature, currentProjection());
    },

    updateRestoreSchedule(
      command: UpdateRestoreScheduleCommand,
      context: AvailabilityCommandContext,
    ): AvailabilityProjection {
      assertContext(context);
      const accepted = assertScheduleCommand(command, context);
      const signature = signatureOf({ kind: 'availability.schedule.update', command: accepted, context });
      const replayed = replay(context.idempotencyKey, signature);
      if (replayed) return replayed;

      options.authorize({ actorId: context.actorId, permission: 'availability.schedule', at: context.at });
      if (context.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');
      const current = schedules.get(accepted.scheduleId);
      if (!current) throw new Error('AVAILABILITY_SCHEDULE_NOT_FOUND');
      if (current.status !== 'active') throw new Error('AVAILABILITY_SCHEDULE_NOT_ACTIVE');

      const nextRevision = revision + 1;
      const schedule: RestoreScheduleProjection = {
        ...current,
        nodeIds: accepted.nodeIds,
        restoreAt: accepted.restoreAt,
        updatedAt: context.at,
        updatedBy: context.actorId,
        sourceSurface: context.sourceSurface,
      };
      const nextSchedules = new Map(schedules);
      nextSchedules.set(schedule.scheduleId, schedule);
      appendControlFact('availability.restore_schedule.changed', nextRevision, context.at, {
        operation: 'update', actorId: context.actorId, sourceSurface: context.sourceSurface, schedule,
      }, `schedule-${accepted.scheduleId}-update`);

      commitState(nextRevision, overrides, nextSchedules, dailyPolicy);
      return commitIdempotency(context.idempotencyKey, signature, currentProjection());
    },

    cancelRestoreSchedule(
      command: CancelRestoreScheduleCommand,
      context: AvailabilityCommandContext,
    ): AvailabilityProjection {
      assertContext(context);
      const scheduleId = assertText(command.scheduleId, 'AVAILABILITY_SCHEDULE_ID_REQUIRED');
      const signature = signatureOf({ kind: 'availability.schedule.cancel', command: { scheduleId }, context });
      const replayed = replay(context.idempotencyKey, signature);
      if (replayed) return replayed;

      options.authorize({ actorId: context.actorId, permission: 'availability.schedule', at: context.at });
      if (context.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');
      const current = schedules.get(scheduleId);
      if (!current) throw new Error('AVAILABILITY_SCHEDULE_NOT_FOUND');
      if (current.status !== 'active') throw new Error('AVAILABILITY_SCHEDULE_NOT_ACTIVE');

      const nextRevision = revision + 1;
      const schedule: RestoreScheduleProjection = {
        ...current,
        status: 'cancelled',
        updatedAt: context.at,
        updatedBy: context.actorId,
        sourceSurface: context.sourceSurface,
      };
      const nextSchedules = new Map(schedules);
      nextSchedules.set(schedule.scheduleId, schedule);
      appendControlFact('availability.restore_schedule.changed', nextRevision, context.at, {
        operation: 'cancel', actorId: context.actorId, sourceSurface: context.sourceSurface, schedule,
      }, `schedule-${scheduleId}-cancel`);

      commitState(nextRevision, overrides, nextSchedules, dailyPolicy);
      return commitIdempotency(context.idempotencyKey, signature, currentProjection());
    },

    configureDailySoldoutRestorePolicy(
      command: ConfigureDailySoldoutRestorePolicyCommand,
      context: AvailabilityCommandContext,
    ): AvailabilityProjection {
      assertContext(context);
      const normalizedTime = command.enabled ? command.restoreLocalTime : null;
      const signature = signatureOf({
        kind: 'availability.daily-policy.configure',
        command: { enabled: command.enabled, restoreLocalTime: normalizedTime },
        context,
      });
      const replayed = replay(context.idempotencyKey, signature);
      if (replayed) return replayed;

      options.authorize({ actorId: context.actorId, permission: 'availability.schedule', at: context.at });
      if (context.sourceSurface !== 'admin') throw new Error('AVAILABILITY_DAILY_POLICY_ADMIN_ONLY');
      if (context.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');
      if (command.enabled && !isLocalTime(command.restoreLocalTime)) {
        throw new Error('AVAILABILITY_DAILY_RESTORE_TIME_INVALID');
      }

      if (command.enabled) {
        const settings = options.getStoreSettings();
        for (const hours of settings.businessHours) {
          if (hours.closed) continue;
          if (!hours.opensAt || command.restoreLocalTime! >= hours.opensAt) {
            throw new Error('AVAILABILITY_DAILY_RESTORE_NOT_BEFORE_OPENING');
          }
        }
      }

      const nextRevision = revision + 1;
      const nextDailyPolicy: DailySoldoutRestorePolicyProjection = {
        enabled: command.enabled,
        restoreLocalTime: command.enabled ? command.restoreLocalTime : null,
        lastAppliedBusinessDate: dailyPolicy.lastAppliedBusinessDate,
        updatedAt: context.at,
        updatedBy: context.actorId,
      };
      appendControlFact('availability.daily_restore_policy.changed', nextRevision, context.at, {
        actorId: context.actorId,
        sourceSurface: context.sourceSurface,
        policy: nextDailyPolicy,
      }, 'daily-policy');

      commitState(nextRevision, overrides, schedules, nextDailyPolicy);
      return commitIdempotency(context.idempotencyKey, signature, currentProjection());
    },

    applyDueRestores(command: ApplyDueRestoresCommand): AvailabilityProjection {
      const now = assertTimestamp(command.now, 'AVAILABILITY_DUE_NOW_INVALID');
      assertRevision(command.expectedRevision);
      const key = assertText(command.idempotencyKey, 'AVAILABILITY_IDEMPOTENCY_REQUIRED');
      const signature = signatureOf({ kind: 'availability.restore.apply-due', command: { now, expectedRevision: command.expectedRevision } });
      const replayed = replay(key, signature);
      if (replayed) return replayed;
      if (command.expectedRevision !== revision) throw new Error('AVAILABILITY_REVISION_CONFLICT');

      const base = options.getBasePolicy();
      const settings = options.getStoreSettings();
      const nextOverrides = new Map(overrides);
      const nextSchedules = new Map(schedules);
      let nextDailyPolicy = clone(dailyPolicy);
      const scheduleResults: Array<{ scheduleId: string; status: 'applied' | 'skipped' }> = [];
      const transitions: Array<{
        nodeId: string;
        previousStatus: AvailabilityStatus;
        newStatus: 'available';
        reasonCode: 'scheduled_restore' | 'daily_soldout_restore';
        scheduleId?: string;
      }> = [];
      let stateChanged = false;

      for (const schedule of [...schedules.values()]
        .filter((candidate) => candidate.status === 'active' && Date.parse(candidate.restoreAt) <= Date.parse(now))
        .sort((left, right) => left.scheduleId.localeCompare(right.scheduleId))) {
        const allKnown = schedule.nodeIds.every((nodeId) => isNodeKnown(nodeId, base));
        const allSoldout = allKnown && schedule.nodeIds.every(
          (nodeId) => effectiveStatusFrom(nextOverrides, nodeId, base) === 'soldout',
        );
        if (!allSoldout) {
          nextSchedules.set(schedule.scheduleId, {
            ...schedule,
            status: 'skipped',
            updatedAt: now,
            updatedBy: 'system',
          });
          scheduleResults.push({ scheduleId: schedule.scheduleId, status: 'skipped' });
          stateChanged = true;
          continue;
        }

        for (const nodeId of schedule.nodeIds) {
          transitions.push({
            nodeId,
            previousStatus: 'soldout',
            newStatus: 'available',
            reasonCode: 'scheduled_restore',
            scheduleId: schedule.scheduleId,
          });
          nextOverrides.set(nodeId, {
            status: 'available',
            changedAt: now,
            changedBy: 'system',
            source: 'system',
            reasonCode: 'scheduled_restore',
            reasonNote: null,
          });
        }
        nextSchedules.set(schedule.scheduleId, {
          ...schedule,
          status: 'applied',
          updatedAt: now,
          updatedBy: 'system',
        });
        scheduleResults.push({ scheduleId: schedule.scheduleId, status: 'applied' });
        stateChanged = true;
      }

      const clock = localStoreClockAt(now, settings);
      const todayHours = settings.businessHours.find((entry) => entry.day === clock.day);
      const dailyDue = Boolean(
        nextDailyPolicy.enabled
        && nextDailyPolicy.restoreLocalTime
        && todayHours
        && !todayHours.closed
        && clock.localTime >= nextDailyPolicy.restoreLocalTime
        && nextDailyPolicy.lastAppliedBusinessDate !== clock.businessDate,
      );

      if (dailyDue) {
        for (const nodeId of Object.keys(base.availability.nodes).sort()) {
          if (!isNodeKnown(nodeId, base)) continue;
          const status = effectiveStatusFrom(nextOverrides, nodeId, base);
          if (status !== 'soldout') continue;
          transitions.push({
            nodeId,
            previousStatus: 'soldout',
            newStatus: 'available',
            reasonCode: 'daily_soldout_restore',
          });
          nextOverrides.set(nodeId, {
            status: 'available',
            changedAt: now,
            changedBy: 'system',
            source: 'system',
            reasonCode: 'daily_soldout_restore',
            reasonNote: null,
          });
        }
        nextDailyPolicy = {
          ...nextDailyPolicy,
          lastAppliedBusinessDate: clock.businessDate,
          updatedAt: now,
          updatedBy: 'system',
        };
        stateChanged = true;
      }

      if (!stateChanged) {
        return commitIdempotency(key, signature, currentProjection());
      }

      const nextRevision = revision + 1;
      if (transitions.length > 0) {
        appendControlFact('availability.changed', nextRevision, now, {
          actorId: 'system',
          sourceSurface: 'system',
          transitions,
        }, 'due-changed');
      }
      appendControlFact('availability.restore_execution.applied', nextRevision, now, {
        source: 'system',
        scheduleResults,
        dailyPolicyApplied: dailyDue,
        businessDate: clock.businessDate,
        transitions,
      }, 'restore-execution');

      commitState(nextRevision, nextOverrides, nextSchedules, nextDailyPolicy);
      return commitIdempotency(key, signature, currentProjection());
    },

    projection(): AvailabilityProjection {
      return clone(currentProjection());
    },

    revision(): number {
      return revision;
    },
  };
}
